import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
const mapboxgl = window.mapboxgl; // caricato da CDN in public/index.html (vedi nota build)
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatErr, MAPBOX_TOKEN } from "@/lib/api";
import { fmtEur, fmtNum } from "@/lib/format";
import { Search, Pencil, Trash2, Square, ChevronLeft, ChevronRight, X, User, Compass, ArrowRight } from "lucide-react";
import { toast } from "sonner";

mapboxgl.accessToken = MAPBOX_TOKEN;

const EXTRA_LABELS = { charging_station: "Stazione di ricarica", backup: "Backup", optimizer: "Ottimizzatore", accessory: "Accessorio", other: "Extra" };

// ── Modello producibilità per inclinazione (mirror del backend) ──
const PROD_TABLE = [[5, 1100], [10, 1200], [20, 1300], [30, 1400]];
const ROW_TABLE = [[5, 0.30], [10, 0.50], [20, 1.10], [30, 1.30]];
function interp(table, t, belowAtZero, aboveSlope) {
  t = Math.max(0, Math.min(60, Number(t) || 0));
  const [t0, v0] = table[0];
  if (t <= t0) return belowAtZero + (v0 - belowAtZero) * (t / t0);
  for (let i = 0; i < table.length - 1; i++) {
    const [a, va] = table[i], [b, vb] = table[i + 1];
    if (t <= b) return va + (vb - va) * (t - a) / (b - a);
  }
  const [lt, lv] = table[table.length - 1];
  return lv + aboveSlope * (t - lt);
}
const prodPerKwp = (tilt) => Math.round(interp(PROD_TABLE, tilt, 1050, -3));
const rowDistanceM = (tilt) => Math.round(interp(ROW_TABLE, tilt, 0.05, 0.02) * 100) / 100;
const azimuthDerate = (az) => Math.max(0.55, 1 - 0.0028 * Math.abs(Number(az) || 0));
const recommendedKwp = (cons, tilt, az) => {
  const spec = prodPerKwp(tilt) * azimuthDerate(az);
  return spec > 0 ? Math.round(((cons || 0) / spec) * 100) / 100 : 0;
};

// ── Geometria layout pannelli (mirror del backend, per preview in tempo reale) ──
function lnglatToLocal(points, origin) {
  const [lat0, lng0] = origin;
  const cosLat = Math.cos((lat0 * Math.PI) / 180);
  return points.map(([lng, lat]) => [(lng - lng0) * 111320 * cosLat, (lat - lat0) * 110540]);
}
function localToLnglat(points, origin) {
  const [lat0, lng0] = origin;
  const cosLat = Math.cos((lat0 * Math.PI) / 180);
  return points.map(([x, y]) => [lng0 + x / (111320 * cosLat), lat0 + y / 110540]);
}
function rot([x, y], a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
}
function pointInPoly([x, y], poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
  }
  return inside;
}
function segIntersect(p1, p2, p3, p4) {
  const d = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const d1 = d(p3, p4, p1), d2 = d(p3, p4, p2), d3 = d(p1, p2, p3), d4 = d(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}
// true se il rettangolo pannello (4 punti) si sovrappone al poligono ostacolo
function rectOverlapsPoly(rect, poly) {
  // 1) un angolo del pannello dentro l'ostacolo
  if (rect.some((c) => pointInPoly(c, poly))) return true;
  // 2) un vertice dell'ostacolo dentro il pannello (ostacolo piccolo dentro il pannello)
  if (poly.some((c) => pointInPoly(c, rect))) return true;
  // 3) intersezione tra i bordi
  for (let i = 0; i < rect.length; i++) {
    const a1 = rect[i], a2 = rect[(i + 1) % rect.length];
    for (let j = 0; j < poly.length; j++) {
      const b1 = poly[j], b2 = poly[(j + 1) % poly.length];
      if (segIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}
function computeLayoutJS(roof, excls, pw, ph, margin, sx, sy, rotDeg, maxPanels) {
  if (!roof || roof.length < 3) return { panels: [], count: 0, maxCount: 0 };
  const origin = [roof[0][1], roof[0][0]];
  const polyXY = lnglatToLocal(roof, origin);
  const exclXY = (excls || []).filter((e) => e.length >= 3).map((e) => lnglatToLocal(e, origin));
  const angle = (rotDeg * Math.PI) / 180;
  const rotated = polyXY.map((p) => rot(p, -angle));
  const xs = rotated.map((p) => p[0]), ys = rotated.map((p) => p[1]);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  const stepX = pw + sx, stepY = ph + sy;
  const panels = [];
  for (let y = miny + margin; y + ph <= maxy - margin; y += stepY) {
    for (let x = minx + margin; x + pw <= maxx - margin; x += stepX) {
      const cr = [[x, y], [x + pw, y], [x + pw, y + ph], [x, y + ph]];
      const corners = cr.map((c) => rot(c, angle));
      if (corners.every((c) => pointInPoly(c, polyXY))) {
        let inExcl = false;
        for (const ex of exclXY) { if (rectOverlapsPoly(corners, ex)) { inExcl = true; break; } }
        if (!inExcl) panels.push(localToLnglat(corners, origin));
      }
    }
  }
  const maxCount = panels.length;
  const limited = maxPanels != null && maxPanels >= 0 ? panels.slice(0, maxPanels) : panels;
  return { panels: limited, count: limited.length, maxCount };
}

// ── Bussola interattiva: mostra N/E/S/O, il sole a Sud (ottimale) e la direzione pannelli ──
function CompassDial({ azimuth, onChange }) {
  const ref = useRef(null);
  const handle = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    const px = (e.touches ? e.touches[0].clientX : e.clientX) - cx;
    const py = (e.touches ? e.touches[0].clientY : e.clientY) - cy;
    // Sud (in basso, py>0) = azimut 0; convenzione PVGIS: Est = -90, Ovest = +90
    let deg = -(Math.atan2(px, py) * 180) / Math.PI;
    deg = Math.max(-90, Math.min(90, Math.round(deg)));
    onChange(deg);
  };
  const az = Number(azimuth) || 0;
  const size = 132, cx = size / 2, cy = size / 2, R = 52;
  // needle: az 0 → Sud (verso il basso)
  const rad = ((az + 90) * Math.PI) / 180; // 0° az → 90° → punta in basso
  const nx = cx + R * Math.cos(rad), ny = cy + R * Math.sin(rad);
  const sunRad = (90 * Math.PI) / 180;
  const sunx = cx + (R + 0) * Math.cos(sunRad), suny = cy + (R) * Math.sin(sunRad);
  return (
    <div className="flex flex-col items-center select-none">
      <svg
        ref={ref}
        width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        onMouseDown={(e) => { handle(e); const mv = (ev) => handle(ev); const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); }; window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up); }}
        onTouchStart={handle} onTouchMove={handle}
        className="cursor-pointer touch-none"
      >
        <circle cx={cx} cy={cy} r={R + 6} fill="#fff" stroke="#e4e4e7" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f4f4f5" strokeWidth="1" />
        {/* sole a Sud */}
        <circle cx={sunx} cy={suny} r="9" fill="#E0A82E" />
        <circle cx={sunx} cy={suny} r="9" fill="none" stroke="#fff" strokeWidth="1.5" />
        {/* needle direzione pannelli */}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#18181b" strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="4" fill="#18181b" />
        {/* cardinali */}
        <text x={cx} y={16} textAnchor="middle" fontSize="11" fontWeight="700" fill="#71717a">N</text>
        <text x={cx} y={size - 7} textAnchor="middle" fontSize="11" fontWeight="700" fill="#E0A82E">S</text>
        <text x={size - 8} y={cy + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#71717a">E</text>
        <text x={9} y={cy + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#71717a">O</text>
      </svg>
    </div>
  );
}

export default function Simulator() {
  const mapEl = useRef(null);
  const map = useRef(null);
  const { company } = useAuth();
  const instPerKwp = Number(company?.install_cost_per_kwp) || 600;
  const instMin = Number(company?.install_cost_min) || 2500;
  const [params] = useSearchParams();
  const urlCustomerId = params.get("customer");

  const [customer, setCustomer] = useState(null);
  const customerId = customer?.id || urlCustomerId;

  const [customers, setCustomers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [custSearch, setCustSearch] = useState("");
  const [custPage, setCustPage] = useState(0);
  const CUST_PAGE_SIZE = 6;
  const [panels, setPanels] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [inverters, setInverters] = useState([]);

  const [center] = useState([9.19, 45.4642]);
  const [drawing, setDrawing] = useState(false);
  const [drawMode, setDrawMode] = useState("roof"); // roof | exclusion
  const [polygon, setPolygon] = useState([]); // current polygon being drawn / committed [lng,lat]
  const [committed, setCommitted] = useState(false);
  const [exclusions, setExclusions] = useState([]); // array of polygon arrays
  const [currentExcl, setCurrentExcl] = useState([]);

  const [selPanel, setSelPanel] = useState(null);
  const [selBattery, setSelBattery] = useState(null);
  const [selInverter, setSelInverter] = useState(null);
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [selExtras, setSelExtras] = useState([]);   // array di id extra selezionati
  const [mountType, setMountType] = useState("tetto");  // tetto | gazebo
  const [gazeboPrice, setGazeboPrice] = useState(0);    // supplemento gazebo (IVA incl.)
  const [orientation, setOrientation] = useState("portrait");
  const [azimuth, setAzimuth] = useState(0);
  const [tilt, setTilt] = useState(20);
  const [spacingX, setSpacingX] = useState(5);   // gap laterale in cm
  const [spacingY, setSpacingY] = useState(110); // gap sopra/sotto (file) in cm — derivato dall'inclinazione
  const [spacingYAuto, setSpacingYAuto] = useState(true); // distanza file automatica da inclinazione
  const [maxPanels, setMaxPanels] = useState(null); // null = tutti
  const [maxAvailable, setMaxAvailable] = useState(0);

  const [mapReady, setMapReady] = useState(false);
  const [layout, setLayout] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [savedSimId, setSavedSimId] = useState(null);

  // Init map
  useEffect(() => {
    if (map.current) return;
    if (!MAPBOX_TOKEN) {
      toast.error("Mapbox token mancante");
      return;
    }
    map.current = new mapboxgl.Map({
      container: mapEl.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: customer?.longitude ? [customer.longitude, customer.latitude] : center,
      zoom: 18,
      pitch: 0,
      preserveDrawingBuffer: true,
    });
    map.current.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), "bottom-right");

    const setupLayers = () => {
      const m = map.current;
      if (!m || m.getSource("roof")) return; // già inizializzato

      // ogni source/layer isolato: un errore su uno non deve bloccare gli altri
      const addSrc = (id) => { try { if (!m.getSource(id)) m.addSource(id, { type: "geojson", data: { type: "FeatureCollection", features: [] } }); } catch (e) { console.warn("addSource", id, e); } };
      const addLyr = (def) => { try { if (!m.getLayer(def.id)) m.addLayer(def); } catch (e) { console.warn("addLayer", def.id, e); } };

      addSrc("roof"); addSrc("panels"); addSrc("vertices"); addSrc("exclusions"); addSrc("excl-vertices");

      addLyr({ id: "roof-fill", type: "fill", source: "roof", paint: { "fill-color": "#E0A82E", "fill-opacity": 0.10 } });
      addLyr({ id: "roof-line", type: "line", source: "roof", paint: { "line-color": "#E0A82E", "line-width": 2.5, "line-dasharray": [2, 1.4] } });
      addLyr({ id: "panels-fill", type: "fill", source: "panels", paint: { "fill-color": "#1E2C55", "fill-opacity": 0.9, "fill-outline-color": "#3A4C86" } });
      addLyr({ id: "panels-line", type: "line", source: "panels", paint: { "line-color": "#5468A8", "line-width": 1, "line-opacity": 0.9 } });
      // vertici PRIMA delle esclusioni (come in main): garantisce i pallini anche se un layer dopo fallisce
      addLyr({ id: "vertices", type: "circle", source: "vertices",
        paint: { "circle-radius": 7, "circle-color": "#FFD84D", "circle-stroke-color": "#18181b", "circle-stroke-width": 3 } });
      addLyr({ id: "exclusions-fill", type: "fill", source: "exclusions", paint: { "fill-color": "#FF4D6D", "fill-opacity": 0.35 } });
      addLyr({ id: "exclusions-line", type: "line", source: "exclusions", paint: { "line-color": "#FF1F4B", "line-width": 2.5 } });
      // pallini ROSSI per i vertici degli ostacoli
      addLyr({ id: "excl-vertices", type: "circle", source: "excl-vertices",
        paint: { "circle-radius": 7, "circle-color": "#FF1F4B", "circle-stroke-color": "#ffffff", "circle-stroke-width": 3 } });

      setMapReady(true);
    };

    if (map.current.isStyleLoaded()) setupLayers();
    map.current.on("load", setupLayers);
    map.current.on("style.load", setupLayers);
    // eslint-disable-next-line
  }, []);

  // Load catalog + customer
  useEffect(() => {
    Promise.all([
      api.get("/products/panels"),
      api.get("/products/batteries"),
      api.get("/products/inverters"),
      api.get("/products/extras"),
    ]).then(([p, b, i, e]) => {
      setPanels(p.data); setBatteries(b.data); setInverters(i.data); setExtrasCatalog(e.data);
      if (p.data[0]) setSelPanel(p.data[0].id);
    });
  }, []);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  useEffect(() => {
    if (!urlCustomerId) return;
    api.get(`/customers/${urlCustomerId}`).then((r) => {
      setCustomer(r.data);
      if (r.data.latitude && r.data.longitude && map.current) {
        map.current.flyTo({ center: [r.data.longitude, r.data.latitude], zoom: 19 });
      }
    });
  }, [urlCustomerId]);

  // Fase contatore: dal cliente, altrimenti derivata dalla potenza (>6kW → trifase)
  const meterPhase = customer?.meter_phase || ((customer?.meter_power_kw || 0) > 6 ? "trifase" : "monofase");
  const compatibleInverters = inverters.filter((i) => i.phases === meterPhase);

  // Distanza tra le file automatica in base all'inclinazione (cm)
  useEffect(() => {
    if (spacingYAuto) setSpacingY(Math.round(rowDistanceM(tilt) * 100));
  }, [tilt, spacingYAuto]);

  // Auto-selezione inverter della fase del contatore
  useEffect(() => {
    if (!inverters.length) return;
    const current = inverters.find((i) => i.id === selInverter);
    if (current && current.phases === meterPhase) return; // già coerente
    const match = compatibleInverters[0];
    if (match) setSelInverter(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inverters, meterPhase]);

  const custName = (c) => c.business_name || `${c.first_name} ${c.last_name}`.trim() || "Senza nome";
  const filteredCustomers = customers.filter((c) => {
    const q = custSearch.trim().toLowerCase();
    if (!q) return true;
    return [custName(c), c.email, c.city, c.address].some((v) => (v || "").toLowerCase().includes(q));
  });
  const custTotalPages = Math.max(1, Math.ceil(filteredCustomers.length / CUST_PAGE_SIZE));
  const custPageSafe = Math.min(custPage, custTotalPages - 1);
  const custPageItems = filteredCustomers.slice(custPageSafe * CUST_PAGE_SIZE, custPageSafe * CUST_PAGE_SIZE + CUST_PAGE_SIZE);

  const selectCustomer = (c) => {
    setCustomer(c);
    setPickerOpen(false);
    setCustSearch("");
    setCustPage(0);
    if (c.latitude && c.longitude && map.current) {
      map.current.flyTo({ center: [c.longitude, c.latitude], zoom: 19 });
    }
  };

  // Drawing handlers
  const handleMapClick = useCallback((e) => {
    if (!drawing) return;
    const pt = [e.lngLat.lng, e.lngLat.lat];
    if (drawMode === "roof" && !committed) setPolygon((p) => [...p, pt]);
    else if (drawMode === "exclusion" && committed) setCurrentExcl((p) => [...p, pt]);
  }, [drawing, committed, drawMode]);

  useEffect(() => {
    if (!map.current) return;
    map.current.on("click", handleMapClick);
    return () => { if (map.current) map.current.off("click", handleMapClick); };
  }, [handleMapClick]);

  // Render roof polygon to map
  useEffect(() => {
    if (!map.current || !map.current.getSource) return;
    const src = map.current.getSource("roof");
    if (!src) return;
    const fc = polygon.length >= 3 ? {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "Polygon", coordinates: [[...polygon, polygon[0]]] } }],
    } : { type: "FeatureCollection", features: [] };
    src.setData(fc);
  }, [polygon, mapReady]);

  // Render vertici: tetto = gialli, ostacoli = rossi (sorgenti separate)
  useEffect(() => {
    if (!map.current || !map.current.getSource) return;
    const toFC = (pts) => ({ type: "FeatureCollection", features: pts.map((p) => ({ type: "Feature", geometry: { type: "Point", coordinates: p } })) });
    const vsrc = map.current.getSource("vertices");
    if (vsrc) vsrc.setData(toFC(polygon));
    const esrc = map.current.getSource("excl-vertices");
    if (esrc) esrc.setData(toFC(drawMode === "exclusion" ? currentExcl : []));
  }, [polygon, currentExcl, drawMode, mapReady]);

  // Render ostacoli / esclusioni in ROSSO sulla mappa (commessi + quello in corso)
  useEffect(() => {
    if (!map.current || !map.current.getSource) return;
    const src = map.current.getSource("exclusions");
    if (!src) return;
    const polys = [...exclusions];
    if (currentExcl.length >= 3) polys.push(currentExcl);
    const features = polys.filter((p) => p.length >= 3).map((p) => ({
      type: "Feature", geometry: { type: "Polygon", coordinates: [[...p, p[0]]] },
    }));
    src.setData({ type: "FeatureCollection", features });
  }, [exclusions, currentExcl, mapReady]);

  // Preview layout pannelli in TEMPO REALE (azimut, margini, n. pannelli) — client-side
  useEffect(() => {
    if (!committed || polygon.length < 3) { setLayout([]); setMaxAvailable(0); return; }
    const panelObj = panels.find((p) => p.id === selPanel);
    if (!panelObj) { setLayout([]); setMaxAvailable(0); return; }
    let pw = (panelObj.width_mm || 1100) / 1000;
    let ph = (panelObj.height_mm || 1700) / 1000;
    if (orientation === "landscape") { const t = pw; pw = ph; ph = t; }
    const res = computeLayoutJS(
      polygon, exclusions, pw, ph, 0.4,
      (Number(spacingX) || 0) / 100, (Number(spacingY) || 0) / 100,
      Number(azimuth) || 0, maxPanels,
    );
    setLayout(res.panels);
    setMaxAvailable(res.maxCount);
  }, [committed, polygon, exclusions, selPanel, orientation, azimuth, spacingX, spacingY, maxPanels, panels]);

  // Render panel layout
  useEffect(() => {
    if (!map.current) return;
    const src = map.current.getSource && map.current.getSource("panels");
    if (!src) return;
    const features = (layout || []).map((rect) => ({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [[...rect, rect[0]]] },
    }));
    src.setData({ type: "FeatureCollection", features });
  }, [layout, mapReady]);

  const startDraw = () => { setDrawing(true); setDrawMode("roof"); setCommitted(false); setPolygon([]); setExclusions([]); setCurrentExcl([]); setLayout([]); setMaxPanels(null); };
  const finishDraw = () => { if (polygon.length < 3) { toast.error("Minimo 3 punti"); return; } setDrawing(false); setCommitted(true); };
  const startExclusion = () => { setDrawing(true); setDrawMode("exclusion"); setCurrentExcl([]); };
  const finishExclusion = () => {
    if (currentExcl.length < 3) { toast.error("Minimo 3 punti"); return; }
    setExclusions((e) => [...e, currentExcl]);
    setCurrentExcl([]);
    setDrawing(false);
    setDrawMode("roof");
  };
  const clearDraw = () => { setDrawing(false); setDrawMode("roof"); setCommitted(false); setPolygon([]); setExclusions([]); setCurrentExcl([]); setLayout([]); setMaxPanels(null); };

  const search = async () => {
    if (!searchVal.trim()) return;
    try {
      const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchVal)}.json?access_token=${MAPBOX_TOKEN}&country=it&limit=1`);
      const d = await r.json();
      if (d.features?.[0]) {
        const [lng, lat] = d.features[0].center;
        map.current.flyTo({ center: [lng, lat], zoom: 19 });
      } else toast.error("Indirizzo non trovato");
    } catch { toast.error("Errore geocoding"); }
  };

  // Salva la simulazione sul backend e calcola i risultati autoritativi. Ritorna {results, simId}.
  const runCalculate = async () => {
    const c = map.current.getCenter();
    const payload = {
      customer_id: customerId,
      name: customer ? `Sim ${customer.business_name || customer.first_name + ' ' + customer.last_name}` : "Simulazione live",
      latitude: c.lat, longitude: c.lng,
      roof_polygon: polygon,
      exclusions: exclusions,
      panel_id: selPanel,
      battery_id: selBattery,
      inverter_id: selInverter,
      panel_orientation: orientation,
      azimuth: parseFloat(azimuth),
      tilt: parseFloat(tilt),
      spacing_x_m: (parseFloat(spacingX) || 0) / 100,
      spacing_y_m: (parseFloat(spacingY) || 0) / 100,
      max_panels: maxPanels,
      mount_type: mountType,
      production_model: "table",
      extra_ids: selExtras,
      annual_consumption_kwh: customer?.annual_consumption_kwh || 3500,
      energy_price_per_kwh: customer?.energy_price_per_kwh || 0.28,
    };
    if (savedSimId) { try { await api.delete(`/simulations/${savedSimId}`); } catch (e) { /* già rimossa */ } }
    const { data: created } = await api.post("/simulations", payload);
    setSavedSimId(created.id);
    const { data } = await api.post(`/simulations/${created.id}/calculate`);
    setLayout(data.panel_layout || []);
    return { results: data.results, simId: created.id };
  };

  const generateQuote = async () => {
    if (!committed || polygon.length < 3) { toast.error("Disegna prima il tetto"); return; }
    if (!customerId) { toast.error("Apri il simulatore da un cliente"); return; }
    if (!selPanel) { toast.error("Seleziona un pannello"); return; }
    setCalculating(true);
    try {
      const { results, simId } = await runCalculate();
      // Capture map screenshot for PDF
      try {
        const canvas = map.current.getCanvas();
        const screenshot = canvas.toDataURL("image/jpeg", 0.7);
        await api.patch(`/simulations/${simId}`, { screenshot_base64: screenshot, exclusions });
      } catch (e) { console.warn("screenshot failed", e); }

      const panelObj = panels.find(p => p.id === selPanel);
      const invObj = inverters.find(i => i.id === selInverter);
      const battObj = batteries.find(b => b.id === selBattery);
      const items = [
        { description: `Pannelli ${results.panel_brand_model} (${results.panels_count} × ${results.panel_power_wp}Wp)`, quantity: results.panels_count, unit_price: panelObj?.sale_price || 220, min_price: panelObj?.min_sale_price ?? null },
        ...(invObj ? [{ description: `Inverter ${invObj.brand} ${invObj.model} (${invObj.phases})`, quantity: 1, unit_price: invObj.sale_price || 1500, min_price: invObj.min_sale_price ?? null }] : []),
        ...(battObj ? [{ description: `Batteria ${battObj.brand} ${battObj.model}`, quantity: 1, unit_price: battObj.sale_price || 4500, min_price: battObj.min_sale_price ?? null }] : []),
        ...selExtras.map((eid) => {
          const e = extrasCatalog.find(x => x.id === eid);
          if (!e) return null;
          return { description: `${EXTRA_LABELS[e.category] || "Accessorio"}: ${e.brand} ${e.model}`, quantity: 1, unit_price: e.sale_price || 0, min_price: e.min_sale_price ?? null };
        }).filter(Boolean),
        { description: "Installazione, pratiche, allaccio rete", quantity: 1, unit_price: Math.max(instMin, results.kwp * instPerKwp) },
      ];
      const profile = customer?.type === "business" || customer?.type === "condominium" || customer?.type === "public" ? "business" : "private";
      const gazebo_cost = mountType === "gazebo" ? (parseFloat(gazeboPrice) || 0) : 0;
      const { data } = await api.post("/quotes", { customer_id: customerId, simulation_id: simId, profile, items, tax_rate: 0.10, gazebo_cost });
      toast.success(`Preventivo ${data.quote_number} creato`);
      window.open(`/app/quotes/${data.id}`, "_blank");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCalculating(false); }
  };

  const previewCount = layout.length;
  const _selPanelObj = panels.find((p) => p.id === selPanel);
  const previewKwp = _selPanelObj ? +((previewCount * _selPanelObj.power_wp) / 1000).toFixed(2) : 0;

  // Area del tetto disegnato (shoelace su coordinate locali in metri)
  const roofAreaM2 = useMemo(() => {
    if (!committed || polygon.length < 3) return 0;
    const origin = [polygon[0][1], polygon[0][0]];
    const xy = lnglatToLocal(polygon, origin);
    let a = 0;
    for (let i = 0, j = xy.length - 1; i < xy.length; j = i++) a += (xy[j][0] + xy[i][0]) * (xy[j][1] - xy[i][1]);
    return Math.abs(a / 2);
  }, [committed, polygon]);

  // Risultati live client-side (specchio del modello "tabella" del backend)
  const live = useMemo(() => {
    const count = previewCount;
    const panel = _selPanelObj;
    if (!count || !panel) return null;
    const kwp = previewKwp;
    const usedArea = count * ((panel.width_mm || 0) / 1000) * ((panel.height_mm || 0) / 1000);
    const annualKwh = kwp * prodPerKwp(tilt) * azimuthDerate(azimuth);
    const consumption = customer?.annual_consumption_kwh || 3500;
    const price = customer?.energy_price_per_kwh || 0.28;
    const scPct = 0.35, exportPrice = 0.08;
    const selfC = Math.min(annualKwh, consumption) * scPct;
    const exported = Math.max(0, annualKwh - selfC);
    const saving = selfC * price + exported * exportPrice;
    const battObj = batteries.find((b) => b.id === selBattery);
    const scB = battObj ? Math.min(0.85, scPct + 0.30) : scPct;
    const selfCB = Math.min(annualKwh, consumption) * scB;
    const exportedB = Math.max(0, annualKwh - selfCB);
    const savingB = selfCB * price + exportedB * exportPrice;
    const invObj = inverters.find((i) => i.id === selInverter);
    const panelCost = count * (panel.sale_price || 220);
    const battCost = battObj ? (battObj.sale_price || 4500) : 0;
    const invCost = invObj ? (invObj.sale_price || 1500) : Math.max(800, kwp * 200);
    const extrasCost = selExtras.reduce((s, id) => s + (extrasCatalog.find((x) => x.id === id)?.sale_price || 0), 0);
    const softCost = Math.max(instMin, kwp * instPerKwp);
    const total = panelCost + battCost + invCost + extrasCost + softCost;
    const payback = saving > 0 ? Math.round((total / saving) * 10) / 10 : null;
    const roi25 = total > 0 ? Math.round(((saving * 25 - total) / total) * 1000) / 10 : 0;
    const co2 = Math.round((annualKwh * 0.4 / 1000) * 100) / 100;
    return {
      count, kwp, roofAreaM2, usedArea, annualKwh, selfC, exported,
      saving: Math.round(saving), savingB: Math.round(savingB), total: Math.round(total),
      payback, roi25, co2,
    };
  }, [previewCount, previewKwp, _selPanelObj, tilt, azimuth, customer, selBattery, selInverter, selExtras, batteries, inverters, extrasCatalog, roofAreaM2, instPerKwp, instMin]);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black" data-testid="page-simulator">
      <div ref={mapEl} className="absolute inset-0" />

      {/* Top toolbar */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 glass-light rounded-full px-4 py-2.5 flex gap-2 items-center text-zinc-900" data-testid="simulator-toolbar">
        <div className="flex items-center gap-2 px-2">
          <Search size={14} className="text-zinc-400" />
          <input
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Cerca indirizzo…"
            className="bg-transparent outline-none text-sm w-56 text-zinc-900 placeholder:text-zinc-400"
            data-testid="search-input"
          />
        </div>
        <span className="w-px h-5 bg-zinc-200" />
        {!drawing && !committed && (
          <button onClick={startDraw} className="px-4 py-1.5 bg-zinc-900 text-white text-xs font-semibold rounded-full flex items-center gap-1.5 hover:bg-zinc-800 transition-colors" data-testid="draw-start-btn">
            <Pencil size={12} /> Disegna tetto
          </button>
        )}
        {drawing && drawMode === "roof" && (
          <>
            <span className="text-xs text-amber-600 font-mono">{polygon.length} punti tetto · click sulla mappa</span>
            <button onClick={finishDraw} className="px-4 py-1.5 bg-zinc-900 text-white text-xs font-semibold rounded-full flex items-center gap-1.5 hover:bg-zinc-800 transition-colors" data-testid="draw-finish-btn">
              <Square size={12} /> Chiudi
            </button>
          </>
        )}
        {drawing && drawMode === "exclusion" && (
          <>
            <span className="text-xs text-rose-500 font-mono">{currentExcl.length} punti esclusione · click sulla mappa</span>
            <button onClick={finishExclusion} className="px-4 py-1.5 bg-rose-500 text-white text-xs font-semibold rounded-full hover:bg-rose-600 transition-colors" data-testid="excl-finish-btn">
              Chiudi esclusione
            </button>
          </>
        )}
        {committed && !drawing && (
          <>
            <button onClick={startExclusion} className="px-3 py-1.5 border border-rose-300 text-rose-500 text-xs rounded-full flex items-center gap-1.5 hover:bg-rose-50 transition-colors" data-testid="add-exclusion-btn">
              + Esclusione (camino/lucernario)
            </button>
            <span className="text-xs text-zinc-400 font-mono">{exclusions.length} ostacoli</span>
            <button onClick={clearDraw} className="px-3 py-1.5 border border-zinc-200 text-zinc-500 text-xs rounded-full flex items-center gap-1.5 hover:bg-zinc-100 hover:text-zinc-900 transition-colors" data-testid="draw-clear-btn">
              <Trash2 size={12} /> Riparti
            </button>
          </>
        )}
      </div>

      {/* Left sidebar */}
      <div className="absolute top-20 left-4 bottom-4 w-80 z-10 bg-white shadow-2xl border border-zinc-200/60 rounded-3xl p-5 flex flex-col gap-4 overflow-y-auto scrollbar-thin text-zinc-900" data-testid="simulator-left-panel">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600">Cliente</div>
          {customer ? (
            <div className="mt-1 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-lg leading-tight text-zinc-900 truncate">
                  {custName(customer)}
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">{[customer.address, customer.city].filter(Boolean).join(", ") || "—"}</div>
              </div>
              <button onClick={() => { setPickerOpen(true); setCustSearch(""); setCustPage(0); }} className="shrink-0 text-xs text-zinc-500 hover:text-zinc-900 underline" data-testid="change-customer-btn">
                Cambia
              </button>
            </div>
          ) : (
            <button onClick={() => { setPickerOpen(true); setCustSearch(""); setCustPage(0); }} className="mt-2 w-full flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-sm text-zinc-500 hover:border-zinc-300 transition-colors" data-testid="pick-customer-btn">
              <User size={14} className="text-zinc-400" /> Seleziona cliente…
            </button>
          )}

          {pickerOpen && (
            <div className="mt-3 bg-white border border-zinc-200 rounded-2xl p-3 shadow-lg" data-testid="customer-picker">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg px-2.5 py-1.5">
                  <Search size={13} className="text-zinc-400" />
                  <input
                    autoFocus
                    value={custSearch}
                    onChange={(e) => { setCustSearch(e.target.value); setCustPage(0); }}
                    placeholder="Cerca nome, email, città…"
                    className="bg-transparent outline-none text-sm w-full text-zinc-900 placeholder:text-zinc-400"
                    data-testid="customer-picker-search"
                  />
                </div>
                <button onClick={() => setPickerOpen(false)} className="text-zinc-400 hover:text-zinc-900 p-1"><X size={15} /></button>
              </div>

              <div className="space-y-1">
                {custPageItems.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">Nessun cliente trovato.</div>}
                {custPageItems.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => selectCustomer(c)}
                    className={`w-full text-left px-2.5 py-2 rounded-lg hover:bg-zinc-50 transition-colors ${customer?.id === c.id ? "bg-amber-50" : ""}`}
                    data-testid={`customer-option-${c.id}`}
                  >
                    <div className="text-sm font-medium text-zinc-900 truncate">{custName(c)}</div>
                    <div className="text-[11px] text-zinc-400 truncate">{[c.city, c.email].filter(Boolean).join(" · ") || "—"}</div>
                  </button>
                ))}
              </div>

              {filteredCustomers.length > CUST_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-100">
                  <button onClick={() => setCustPage((p) => Math.max(0, p - 1))} disabled={custPageSafe === 0} className="p-1 text-zinc-500 disabled:opacity-30 hover:text-zinc-900"><ChevronLeft size={16} /></button>
                  <span className="font-mono text-[11px] text-zinc-400">{custPageSafe + 1} / {custTotalPages} · {filteredCustomers.length} clienti</span>
                  <button onClick={() => setCustPage((p) => Math.min(custTotalPages - 1, p + 1))} disabled={custPageSafe >= custTotalPages - 1} className="p-1 text-zinc-500 disabled:opacity-30 hover:text-zinc-900"><ChevronRight size={16} /></button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-zinc-200/80 pt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-2">Configurazione</div>
          <label className="text-xs text-zinc-500">Pannello</label>
          <select value={selPanel || ""} onChange={(e) => setSelPanel(e.target.value)} className="w-full mt-1 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-sm" data-testid="select-panel">
            <option value="">— scegli —</option>
            {panels.map((p) => <option key={p.id} value={p.id}>{p.brand} {p.model} · {p.power_wp}W</option>)}
          </select>

          <label className="text-xs text-zinc-500 mt-3 block">Batteria (opzionale)</label>
          <select value={selBattery || ""} onChange={(e) => setSelBattery(e.target.value || null)} className="w-full mt-1 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-sm" data-testid="select-battery">
            <option value="">— senza batteria —</option>
            {batteries.map((b) => <option key={b.id} value={b.id}>{b.brand} {b.model} · {b.capacity_kwh}kWh</option>)}
          </select>

          <div className="flex items-center justify-between mt-3">
            <label className="text-xs text-zinc-500">Inverter</label>
            <span className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200/70" title="Selezionato automaticamente in base alla fase del contatore">
              auto · {meterPhase}
            </span>
          </div>
          <select value={selInverter || ""} onChange={(e) => setSelInverter(e.target.value || null)} className="w-full mt-1 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-sm" data-testid="select-inverter">
            <option value="">— auto —</option>
            {(compatibleInverters.length ? compatibleInverters : inverters).map((i) => <option key={i.id} value={i.id}>{i.brand} {i.model} · {i.ac_power_kw}kW</option>)}
          </select>
          {compatibleInverters.length === 0 && inverters.length > 0 && (
            <p className="text-[10px] text-rose-500 mt-1">Nessun inverter {meterPhase} a catalogo: aggiungilo dal Catalogo.</p>
          )}

          {/* Sezione installazione */}
          <div className="mt-5 pt-4 border-t border-zinc-200/80">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-2">Installazione</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[["tetto", "Tetto"], ["gazebo", "Gazebo"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setMountType(v)}
                  className={`rounded-xl px-2 py-2 text-sm font-medium border transition-colors ${mountType === v ? "bg-zinc-900 text-white border-zinc-900" : "bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}
                  data-testid={`mount-${v}`}>{l}</button>
              ))}
            </div>
            {mountType === "gazebo" && (
              <div className="mt-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-zinc-500">Supplemento gazebo (€, IVA incl.)</label>
                  <span className="font-mono text-[11px] text-amber-600">{fmtEur(gazeboPrice)}</span>
                </div>
                <input type="number" min="0" step="50" value={gazeboPrice}
                  onChange={(e) => setGazeboPrice(parseFloat(e.target.value) || 0)}
                  className="w-full mt-1 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-sm font-mono" data-testid="gazebo-price" />
                <p className="text-[10px] text-zinc-400 mt-1">Mostrato come supplemento a parte, oltre al "chiavi in mano".</p>
              </div>
            )}
            <label className="text-xs text-zinc-500 mt-3 block">Orientamento pannelli</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {[["portrait", "Verticale"], ["landscape", "Orizzontale"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setOrientation(v)}
                  className={`rounded-xl px-2 py-2 text-sm font-medium border transition-colors ${orientation === v ? "bg-zinc-900 text-white border-zinc-900" : "bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}
                  data-testid={`orient-${v}`}>{l}</button>
              ))}
            </div>
          </div>

          {/* Accessori / extra: ricarica, backup, ottimizzatori */}
          {extrasCatalog.length > 0 && (
            <div className="mt-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600 mb-2">Accessori</div>
              <div className="space-y-1.5 max-h-44 overflow-y-auto scrollbar-thin pr-1">
                {extrasCatalog.map((e) => {
                  const on = selExtras.includes(e.id);
                  return (
                    <button key={e.id} type="button"
                      onClick={() => setSelExtras((s) => on ? s.filter((x) => x !== e.id) : [...s, e.id])}
                      className={`w-full flex items-center justify-between gap-2 rounded-xl px-2.5 py-1.5 text-left text-xs border transition-colors ${on ? "bg-emerald-50 border-emerald-300" : "bg-zinc-50 border-zinc-200 hover:border-zinc-300"}`}
                      data-testid={`extra-${e.id}`}>
                      <span className="min-w-0">
                        <span className="block font-medium text-zinc-800 truncate">{e.brand} {e.model}</span>
                        <span className="block text-[10px] text-zinc-400">{EXTRA_LABELS[e.category] || "Extra"}{e.phases && e.phases !== "any" ? ` · ${e.phases}` : ""}</span>
                      </span>
                      <span className="font-mono text-[11px] text-amber-600 shrink-0">{fmtEur(e.sale_price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bussola + Azimut */}
          <div className="mt-5 pt-4 border-t border-zinc-200/80">
            <div className="flex items-center gap-1.5 mb-2">
              <Compass size={12} className="text-amber-600" />
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600">Bussola · direzione del sole</label>
            </div>
            <CompassDial azimuth={azimuth} onChange={(v) => setAzimuth(v)} />
            <div className="text-center text-[11px] text-zinc-400 mt-1">
              Il <span className="text-amber-600 font-medium">●</span> indica il Sud. Allinea i pannelli al sole.
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <label className="text-xs text-zinc-500">Azimut (rotazione)</label>
                <span className="font-mono text-xs text-amber-600">{azimuth}°</span>
              </div>
              <input type="range" min="-90" max="90" step="1" value={azimuth} onChange={(e) => setAzimuth(parseInt(e.target.value, 10))} className="w-full accent-[#E0A82E]" data-testid="azimuth-slider" />
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between">
              <label className="text-xs text-zinc-500">Inclinazione</label>
              <span className="font-mono text-xs text-amber-600">{tilt}°</span>
            </div>
            <input type="range" min="0" max="60" value={tilt} onChange={(e) => setTilt(parseInt(e.target.value, 10))} className="w-full accent-[#E0A82E]" data-testid="tilt-slider" />
            {/* Producibilità e distanza file derivate dall'inclinazione */}
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="tilt-derived">
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-2.5 py-1.5">
                <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-400">Producibilità</div>
                <div className="font-mono text-sm text-amber-600">{prodPerKwp(tilt)} <span className="text-[10px] text-zinc-400">kWh/kWp</span></div>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-2.5 py-1.5">
                <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-400">Distanza file</div>
                <div className="font-mono text-sm text-zinc-900">{rowDistanceM(tilt).toFixed(2).replace(".", ",")} <span className="text-[10px] text-zinc-400">m</span></div>
              </div>
            </div>
            {customer?.annual_consumption_kwh ? (
              <div className="mt-2 bg-amber-50/70 border border-amber-200/70 rounded-xl px-3 py-2 text-[11px] text-zinc-600">
                Potenza consigliata dal consumo ({fmtNum(customer.annual_consumption_kwh)} kWh):
                <span className="font-mono text-amber-600 font-semibold"> {recommendedKwp(customer.annual_consumption_kwh, tilt, azimuth)} kWp</span>
              </div>
            ) : null}
          </div>

          {/* Distanza tra le file + margine laterale */}
          <div className="mt-5 pt-4 border-t border-zinc-200/80">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600">Spaziatura</div>
              <label className="flex items-center gap-1 text-[10px] text-zinc-500 cursor-pointer">
                <input type="checkbox" checked={spacingYAuto} onChange={(e) => setSpacingYAuto(e.target.checked)} className="accent-[#E0A82E]" data-testid="spacing-y-auto" />
                file da inclinazione
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-zinc-500">Tra le file</label>
                  <span className="font-mono text-[11px] text-amber-600">{spacingY} cm</span>
                </div>
                <input type="range" min="0" max="200" value={spacingY} disabled={spacingYAuto}
                  onChange={(e) => setSpacingY(parseInt(e.target.value, 10))}
                  className="w-full accent-[#E0A82E] disabled:opacity-40" data-testid="spacing-y-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-zinc-500">Laterale</label>
                  <span className="font-mono text-[11px] text-amber-600">{spacingX} cm</span>
                </div>
                <input type="range" min="0" max="50" value={spacingX} onChange={(e) => setSpacingX(parseInt(e.target.value, 10))} className="w-full accent-[#E0A82E]" data-testid="spacing-x-slider" />
              </div>
            </div>
          </div>

          {/* Numero pannelli */}
          {committed && maxAvailable > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600">Numero pannelli</div>
                <button onClick={() => setMaxPanels(null)} className={`text-[11px] underline ${maxPanels === null ? "text-zinc-300 cursor-default" : "text-zinc-500 hover:text-zinc-900"}`} disabled={maxPanels === null}>
                  tutti ({maxAvailable})
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range" min="1" max={maxAvailable}
                  value={maxPanels == null ? maxAvailable : Math.min(maxPanels, maxAvailable)}
                  onChange={(e) => setMaxPanels(parseInt(e.target.value, 10))}
                  className="flex-1 accent-[#E0A82E]" data-testid="max-panels-slider"
                />
                <input
                  type="number" min="1" max={maxAvailable}
                  value={maxPanels == null ? maxAvailable : Math.min(maxPanels, maxAvailable)}
                  onChange={(e) => { const v = parseInt(e.target.value, 10); setMaxPanels(isNaN(v) ? null : Math.max(1, Math.min(maxAvailable, v))); }}
                  className="w-16 bg-zinc-50 border border-zinc-200 rounded-lg px-2 py-1.5 text-sm font-mono text-center"
                  data-testid="max-panels-input"
                />
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Right sidebar */}
      <div className="absolute top-20 right-4 w-80 max-h-[calc(100vh-6rem)] z-10 bg-[#18181B] text-white rounded-3xl p-5 shadow-2xl border border-white/10 flex flex-col gap-4 overflow-y-auto scrollbar-thin" data-testid="simulator-right-panel">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Risultati live</span>
        </div>

        {!live && <div className="text-sm text-zinc-400 leading-relaxed">Disegna l'area sul tetto per vedere impianto, produzione e ROI in tempo reale.</div>}

        {live && (
          <>
            {/* Impianto */}
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Impianto</div>
              <div className="flex items-baseline gap-1.5 flex-wrap mt-1">
                <span className="font-mono font-semibold text-3xl">{live.count}</span>
                <span className="text-xs text-zinc-500">pannelli</span>
                <span className="text-zinc-600 mx-0.5">·</span>
                <span className="font-mono font-semibold text-3xl text-amber-400">{live.kwp}</span>
                <span className="text-xs text-zinc-500">kWp</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">Area utile</div>
                <div className="font-mono text-base mt-0.5">{fmtNum(Math.round(live.roofAreaM2))} <span className="text-[10px] text-zinc-500">m²</span></div>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">Coperta</div>
                <div className="font-mono text-base mt-0.5">{fmtNum(Math.round(live.usedArea))} <span className="text-[10px] text-zinc-500">m²</span></div>
              </div>
            </div>

            {/* Produzione */}
            <div className="pt-3 border-t border-white/10">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-500">Produzione · tabella inclinazione</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-mono text-2xl font-semibold">{fmtNum(Math.round(live.annualKwh))}</span>
                <span className="text-xs text-zinc-500">kWh · stima annua</span>
              </div>
              <div className="mt-3 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-zinc-400">Autoconsumo</span><span className="font-mono">{fmtNum(Math.round(live.selfC))} kWh</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Immesso rete</span><span className="font-mono">{fmtNum(Math.round(live.exported))} kWh</span></div>
              </div>
            </div>

            {/* Economia */}
            <div className="pt-3 border-t border-white/10">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Economia</div>
              <div className="mt-2 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Risparmio/anno</span><span className="font-mono text-emerald-400">{fmtEur(live.saving)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Con batteria</span><span className="font-mono text-sky-400">{fmtEur(live.savingB)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Investimento</span><span className="font-mono">{fmtEur(live.total)}</span></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">Payback</div>
                <div className="font-mono text-base mt-0.5">{live.payback != null ? String(live.payback).replace(".", ",") : "—"} <span className="text-[10px] text-zinc-500">anni</span></div>
              </div>
              <div className="bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">ROI 25 anni</div>
                <div className="font-mono text-base mt-0.5 text-emerald-400">{String(live.roi25).replace(".", ",")}%</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">CO₂ evitata / anno</div>
              <div className="font-mono text-base">{String(live.co2).replace(".", ",")} <span className="text-[10px] text-zinc-500">t</span></div>
            </div>

            <button onClick={generateQuote} disabled={calculating || !committed}
              className="mt-1 bg-amber-400 text-zinc-900 font-semibold rounded-full py-3 text-sm flex items-center justify-center gap-2 hover:bg-amber-300 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="generate-quote-btn">
              {calculating ? "Generazione…" : <>Genera preventivo <ArrowRight size={15} /></>}
            </button>
          </>
        )}
      </div>

    </div>
  );
}
